import { computed } from 'vue';
import type { AlertType } from '@flash-global66/g-dialog-alert';
import { useRemoteConfigStore } from '../stores/remoteConfig.store';
import { useAccountStore } from '@/stores/account';

export interface ServiceAlertMessage {
  enabled: boolean;
  title: string;
  description: string;
  type: AlertType;
  buttonText: string;
}

/**
 * Obtiene la configuración de alerta de servicio según el país del usuario.
 * Si el país tiene configuración específica, la usa. Si no, usa la configuración de 'all'.
 */
export function useServiceAlert() {
  const remoteConfigStore = useRemoteConfigStore();
  const accountStore = useAccountStore();

  const serviceAlertConfig = remoteConfigStore.getRemoteConfigValue('feature_service_alert');

  const serviceAlert = computed<ServiceAlertMessage>(() => {
    const config = serviceAlertConfig.value as Record<string, any>;
    const countryCode = accountStore.getCompanyCountry?.toLowerCase() || '';

    if (!config) {
      return {
        enabled: false,
        title: '',
        description: '',
        type: 'info' as AlertType,
        buttonText: ''
      };
    }

    const countryConfig = config[countryCode];
    const allConfig = config.all;

    if (countryConfig?.enabled === false) {
      return {
        enabled: false,
        title: '',
        description: '',
        type: 'info' as AlertType,
        buttonText: ''
      };
    }

    if (!countryConfig) {
      return {
        enabled: allConfig?.enabled ?? false,
        title: allConfig?.title ?? '',
        description: allConfig?.description ?? '',
        type: (allConfig?.type as AlertType) ?? 'info',
        buttonText: allConfig?.buttonText ?? ''
      };
    }

    return {
      enabled: countryConfig.enabled ?? allConfig?.enabled ?? false,
      title: countryConfig.title ?? allConfig?.title ?? '',
      description: countryConfig.description ?? allConfig?.description ?? '',
      type: (countryConfig.type as AlertType) ?? (allConfig?.type as AlertType) ?? 'info',
      buttonText: countryConfig.buttonText ?? allConfig?.buttonText ?? ''
    };
  });

  return {
    serviceAlert,
  };
}
